import { PaymentAllocationMethod } from '@rematter/frontend-lib-api-client';
import {
  Col,
  formatMoney,
  LocalizedDate,
  NumberField,
  Row,
  TextField,
} from '@rematter/ui-web';

import type {
  AccountGroup,
  PaymentItem,
} from '../../_state/paymentAllocation.state';
import { useTranslations } from '../../i18n/useTranslations';
import { getSortedItems } from '../../utils';

interface AccountGroupCardProps<T extends PaymentItem> {
  variant: 'bill' | 'invoice';
  group: AccountGroup<T>;
  groupAllocation: number;
  showItemDetails: boolean;
  isWirePayment: boolean;
  isMultiGroup: boolean;
  groupWireNumbers: Map<string, string>;
  selectedAllocationMethod: PaymentAllocationMethod | null;
  itemAllocations: Map<string, number>;
  updateAccountAllocation: (args: {
    accountKey: string;
    value: number;
  }) => void;
  updateGroupWireNumber: (args: {
    accountKey: string;
    value: string | null;
  }) => void;
  updateItemAllocation: (args: { itemId: string; value: number }) => void;
}

//
// ⚛️ ⚛️ ⚛️ ACCOUNT GROUP CARD ⚛️ ⚛️ ⚛️
//
export const AccountGroupCard = <T extends PaymentItem>({
  variant,
  group,
  groupAllocation,
  showItemDetails,
  isWirePayment,
  isMultiGroup,
  groupWireNumbers,
  selectedAllocationMethod,
  itemAllocations,
  updateAccountAllocation,
  updateGroupWireNumber,
  updateItemAllocation,
}: AccountGroupCardProps<T>) => {
  // Translations
  const t = useTranslations();

  const isBill = variant === 'bill';
  const fields = isBill
    ? t.bills.paymentDrawer.fields
    : t.invoices.paymentDrawer.fields;
  const itemCount = isBill
    ? t.bills.list.groupHeader.billCount
    : t.invoices.list.groupHeader.invoiceCount;
  const documentNumber = isBill
    ? t.bills.list.documentNumber
    : t.invoices.list.documentNumber;

  return (
    <Col gap2 className="@AccountGroupCard rounded border px-3 py-3">
      <Row xBetween>
        <div>
          <p className="text-sm font-medium">{group.name}</p>
          <p className="text-xs text-foreground/50">
            {itemCount(group.items.length)}
          </p>
        </div>
        <p className="text-xs text-foreground/50">
          {formatMoney(group.total, group.currencyCode)} {fields.due}
        </p>
      </Row>
      <NumberField
        label={fields.paymentAmount}
        value={groupAllocation}
        onChange={(value) =>
          updateAccountAllocation({
            accountKey: group.accountKey,
            value: value ?? 0,
          })
        }
        minValue={0}
        maxValue={group.total}
        formatOptions={{
          style: 'currency',
          currency: group.currencyCode,
        }}
      />
      {isWirePayment && isMultiGroup && (
        <TextField
          label={fields.wireNumber}
          value={groupWireNumbers.get(group.accountKey) ?? ''}
          onChange={(value) =>
            updateGroupWireNumber({
              accountKey: group.accountKey,
              value: value || null,
            })
          }
        />
      )}
      {showItemDetails && (
        <Col gap2 className="mt-1 border-t pt-2">
          {getSortedItems(group.items, selectedAllocationMethod).map((item) => (
            <Col key={item.id} gap1>
              <Row gap2>
                <div className="flex-1">
                  <NumberField
                    label={documentNumber(item.documentNumber)}
                    value={itemAllocations.get(item.id) ?? 0}
                    onChange={(value) =>
                      updateItemAllocation({
                        itemId: item.id,
                        value: value ?? 0,
                      })
                    }
                    minValue={0}
                    maxValue={item.amount.amount}
                    formatOptions={{
                      style: 'currency',
                      currency: item.amount.currencyCode,
                    }}
                  />
                </div>
                <p className="shrink-0 text-xs text-foreground/50">
                  / {formatMoney(item.amount.amount, item.amount.currencyCode)}
                </p>
              </Row>
              <p className="pl-1 text-xs text-foreground/50">
                {item.dueDate ? (
                  <>
                    {fields.duePrefix}{' '}
                    <LocalizedDate date={item.dueDate} dateStyle="medium" />
                  </>
                ) : (
                  fields.noDueDate
                )}
              </p>
            </Col>
          ))}
        </Col>
      )}
    </Col>
  );
};
